// apps/web/src/features/russia-requests/components/editor/RussiaEditorFilesCard.tsx
//
// COMPLEX file-attachments block for the Russia editor (#1546). Mirrors
// the China `RequestFilesField` UI (upload + list + download + remove)
// retargeted to the Russia file endpoints. Files attach to the editor's
// saved DRAFT id via `ensurePersisted` (same lifecycle as SIMPLE
// positions); upload/list/remove sync lives in `useRussiaDraftFiles`.
//
// @aiSecurity signed download URLs are bearer-like and stay in in-memory
//   component state only; they are never logged or persisted.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RUSSIA_REQUEST_FILE_MAX_PER_REQUEST, type RussiaRequestFile } from '@magimp/shared';
import { Button, EmptyState, Icon, IconButton } from '../../../../ui/index.js';
import { formatDateTime, formatSize } from '../../../../i18n/index.js';
import { russiaRequestFileDownloadUrl } from '../../../../api/russia-requests/files.js';
import { Card } from './EditorPrimitives.js';
import {
  useRussiaDraftFiles,
  type RussiaDraftFilesError,
  type UseRussiaDraftFilesOptions,
} from './useRussiaDraftFiles.js';

export interface RussiaEditorFilesCardProps {
  persistedId: string | null;
  ensurePersisted: UseRussiaDraftFilesOptions['ensurePersisted'];
}

function isImage(contentType: string): boolean {
  return contentType.startsWith('image/');
}

// Map each stable error code to its literal i18n key so the strongly-typed
// `t()` can validate the key at compile time.
const ERROR_KEYS = {
  wrongType: 'editor.files.errors.wrongType',
  tooLarge: 'editor.files.errors.tooLarge',
  uploadFailed: 'editor.files.errors.uploadFailed',
  listFailed: 'editor.files.errors.listFailed',
  removeFailed: 'editor.files.errors.removeFailed',
} as const satisfies Record<RussiaDraftFilesError, string>;

export function RussiaEditorFilesCard({
  persistedId,
  ensurePersisted,
}: RussiaEditorFilesCardProps) {
  const { t } = useTranslation('russiaRequests');
  const files = useRussiaDraftFiles({ persistedId, ensurePersisted });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // INVARIANT: with no persisted id the control is still usable — the first
  //   upload materializes the draft via `ensurePersisted`. It is inert only
  //   while an upload is in flight or the cap is reached.
  const addDisabled = files.uploading || files.capReached;

  return (
    <Card title={t('editor.files.title')}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          disabled={addDisabled}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void files.addFile(file);
            e.target.value = '';
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Button
            kind="secondary"
            size="sm"
            icon="upload-cloud"
            disabled={addDisabled}
            onClick={() => fileInputRef.current?.click()}
            data-testid="russia-files-add"
          >
            {files.uploading ? t('editor.files.uploading') : t('editor.files.add')}
          </Button>
          <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
            {t('editor.files.usage', {
              count: files.files.length,
              max: RUSSIA_REQUEST_FILE_MAX_PER_REQUEST,
            })}
          </span>
        </div>

        {files.capReached && (
          <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>{t('editor.files.capReached')}</span>
        )}

        {files.error && (
          <span
            role="alert"
            style={{
              fontSize: 11,
              color: 'var(--status-error-fg)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Icon name="alert-triangle" size={12} />
            {t(ERROR_KEYS[files.error])}
          </span>
        )}

        {persistedId !== null && files.files.length === 0 && !files.error && (
          <EmptyState icon="paperclip" title={t('editor.files.empty')} />
        )}

        {files.files.length > 0 && (
          <ul
            data-testid="russia-files-list"
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            {files.files.map((file) => (
              <RussiaFileRow
                key={file.id}
                requestId={persistedId}
                file={file}
                onRemove={() => void files.removeFile(file.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

function RussiaFileRow({
  requestId,
  file,
  onRemove,
}: {
  requestId: string | null;
  file: RussiaRequestFile;
  onRemove: () => void;
}) {
  const { t } = useTranslation('russiaRequests');
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const image = isImage(file.contentType);

  // Image rows resolve a presigned URL once for inline preview. Document
  // rows resolve on demand (download click) only.
  useEffect(() => {
    if (!image || requestId === null) return;
    let cancelled = false;
    void russiaRequestFileDownloadUrl(requestId, file.id).then(
      (res) => {
        if (!cancelled) setThumbUrl(res.downloadUrl);
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [image, requestId, file.id]);

  async function onDownload() {
    if (requestId === null) return;
    setBusy(true);
    try {
      const res = await russiaRequestFileDownloadUrl(requestId, file.id);
      window.open(res.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch {
      // A failed download leaves the row untouched.
    } finally {
      setBusy(false);
    }
  }

  return (
    <li
      data-testid="russia-file-row"
      data-file-id={file.id}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 8px',
        borderRadius: 'var(--r-2)',
        border: '1px solid var(--border-1)',
        background: 'var(--bg-1)',
      }}
    >
      {image && thumbUrl ? (
        <img
          src={thumbUrl}
          alt={file.fileName}
          style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 'var(--r-1)' }}
        />
      ) : (
        <Icon name={image ? 'image' : 'file-text'} size={16} />
      )}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span
          title={file.fileName}
          style={{
            fontSize: 13,
            color: 'var(--fg-1)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {file.fileName}
        </span>
        <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
          {formatSize(file.contentLength)} · {formatDateTime(file.createdAt)}
        </span>
      </div>
      <Button
        kind="ghost"
        size="sm"
        icon="download"
        aria-label={t('editor.files.download')}
        disabled={busy || requestId === null}
        onClick={() => void onDownload()}
        data-testid="russia-file-download"
      />
      <IconButton
        icon="trash-2"
        size={24}
        label={t('editor.files.remove')}
        onClick={onRemove}
        data-testid="russia-file-remove"
      />
    </li>
  );
}
